import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { Chip, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { SearchableLanguage } from "../../api/person";

type PersonLanguageProps = {
	languages: SearchableLanguage[];
	/** iso639 code of the language the outreach pitch will use — highlighted in the list. */
	pitchLanguageCode?: string | null;
};

export const PersonLanguage = ({
	languages,
	pitchLanguageCode,
}: PersonLanguageProps) => {
	if (!languages.length) {
		return null;
	}

	const pitchCode = pitchLanguageCode?.toLowerCase() ?? null;

	return (
		<Paper sx={{ p: 2 }} data-component="person-language-paper">
			<Typography
				variant="overline"
				color="text.secondary"
				sx={{
					fontWeight: "bold",
					letterSpacing: "0.5px",
					mb: 1.5,
					display: "block",
				}}
				data-component="languages-section-label"
			>
				Languages
			</Typography>
			<Stack spacing={0.5} data-component="languages-list">
				{languages.map((language, idx) => {
					const isPitchLanguage =
						pitchCode !== null &&
						language.iso639_code?.toLowerCase() === pitchCode;
					return (
						<Stack
							key={`${language.iso639_code}-${idx}`}
							direction="row"
							spacing={0.5}
							alignItems="center"
							data-component="person-language-item"
						>
							{language.is_searchable ? (
								<Tooltip
									title={
										language.source === "inferred"
											? "Inferred from profile"
											: "Searchable"
									}
								>
									{language.source === "inferred" ? (
										<AutoAwesomeIcon
											color="info"
											sx={{ fontSize: 16 }}
											data-component="inferred-language-icon"
										/>
									) : (
										<CheckCircleIcon
											color="success"
											sx={{ fontSize: 16 }}
											data-component="declared-language-icon"
										/>
									)}
								</Tooltip>
							) : (
								<Tooltip title="Not searchable">
									<RemoveCircleOutlineIcon
										color="disabled"
										sx={{ fontSize: 16 }}
										data-component="not-searchable-language-icon"
									/>
								</Tooltip>
							)}
							<Typography
								variant="body2"
								color="text.primary"
								data-component="language-text"
								sx={
									isPitchLanguage ? { fontWeight: "bold" } : undefined
								}
							>
								{language.language}
								{language.proficiency && ` · ${language.proficiency}`}
							</Typography>
							{isPitchLanguage && (
								<Chip
									label="Pitch"
									size="small"
									color="primary"
									data-component="pitch-language-chip"
									sx={{
										height: 18,
										"& .MuiChip-label": { px: 0.75 },
									}}
								/>
							)}
						</Stack>
					);
				})}
			</Stack>
		</Paper>
	);
};
